<template>
  <div :class="{ 'is-mobile': mobileView, 'is-desktop': desktopView }">
    <div class="row" v-if="mobileView">
      <div class="small-12 columns">
        <div class="flex-row mb-0 mt-4 align-items-center">
          <h2 class="is-marginless mr-2">All hackathons</h2>

          <result-count class="text-right"></result-count>
        </div>
      </div>
    </div>

    <search-bar ref="searchBarRef"></search-bar>

    <div v-if="mobileView">
      <hackathon-pills></hackathon-pills>
      <hackathon-active-filters></hackathon-active-filters>
      <div class="px-4 pt-5">
        <eligibility-toggle :filter="eligibilityToggleFilter"></eligibility-toggle>
        <managed-by-devpost-check-box :filter="managedByDevpostBadgeFilter" :is-mobile="mobileView"></managed-by-devpost-check-box>
      </div>
    </div>

    <div class="row mt-5" id="results-and-filters">
      <hackathon-filters v-if="desktopView"></hackathon-filters>

      <div class="columns pb-7" :class="{ 'large-9': desktopView, 'large-12': mobileView }">
        <div v-if="desktopView">
          <hackathon-sorting></hackathon-sorting>
        </div>

        <!-- Discord banner should appear between sorting and results -->
        <discord-banner mixpanel-source="hackathon-search"/>

        <div class="initializing-state text-center" v-if="initializing">
          <i class="fas fa-sync fa-spin"></i>
        </div>

        <div v-if="!initializing" :class="[{ 'is-loading': isLoading }]" class="hackathons-container">

          <alert-box icon="info-circle" isSmall v-if="fuzzySearch" class="mb-4">
            The search '{{lastSearchQuery}}' did not return any results. Showing similar matches.
          </alert-box>

          <alert-box level="error" icon="exclamation-circle" v-if="searchError" class="mb-4">
            Oops! An error occurred. Please try again later.
          </alert-box>

          <hackathon-result
            v-for="hackathon in hackathons"
            :hackathon="hackathon"
            :key="hackathon.id"
            class="mb-5"
            :refParams="{ ref_feature: 'challenge', ref_medium: 'discover' }">
          </hackathon-result>

          <div v-if="isLoading && currentPage != 1" class="text-center infinite-scroll-loading">
            <i class="fas fa-sync fa-spin"></i>
          </div>

          <div v-if="showEndOfResults" class="text-center">
            <p class="faded">End of results</p>
            <button class="radius" @click.prevent="searchAgain">
              Search again
            </button>
          </div>

          <hackathon-empty-state v-if="!anyHackathons"></hackathon-empty-state>
        </div>
      </div>

      <div v-if="mobileView && anyPillOpened" id="pill-overlay" @click.prevent></div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watchEffect, reactive, defineAsyncComponent } from 'vue';
import { useStore } from 'vuex';

import HackathonResult from '@/components/HackathonResult.vue';
import HackathonFilters from '@/components/HackathonFilters.vue';
import SearchBar from '@/components/SearchBar.vue';
import HackathonEmptyState from '@/components/HackathonEmptyState.vue';
import EligibilityToggle from '@/components/EligibilityToggle.vue';
import ResultCount from '@/components/ResultCount.vue';
import AlertBox from '@/components/AlertBox.vue';

import InfiniteScroll from '@/scripts/infinite_scroll';
import useIsMobile from '@/scripts/use_is_mobile';

import isEmpty from 'lodash/isEmpty';
import ManagedByDevpostCheckBox from "./ManagedByDevpostCheckBox.vue";

export default {
  name: 'HackathonSearch',
  components: {
    ManagedByDevpostCheckBox,
    HackathonResult,
    HackathonFilters,
    SearchBar,
    HackathonEmptyState,
    ResultCount,
    AlertBox,
    EligibilityToggle,
    HackathonPills: defineAsyncComponent(() => import('@/components/HackathonPills.vue') ),
    HackathonSorting: defineAsyncComponent(() => import('@/components/HackathonSorting.vue') ),
    HackathonActiveFilters: defineAsyncComponent(() => import('@/components/HackathonActiveFilters.vue')),
    DiscordBanner: defineAsyncComponent(() => import('@/components/DiscordBanner.vue'))
  },
  setup() {
    const store = useStore();

    const { mobileView, desktopView } = useIsMobile();

    const initializing = ref(true);

    store.dispatch("initialization/initialiseStore");

    store.dispatch("hackathons/search").
    then(() => initializing.value = false);

    store.dispatch("filters/fetchThemes");

    const lastPageReached = computed(() => store.getters["hackathons/lastPageReached"]);

    const currentPage = computed(() => store.getters["hackathons/getCurrentPage"]);

    let loadMoreHackathons = async () => {
      await store.dispatch("hackathons/search", { page: currentPage.value + 1 })
    }

    onMounted(() => {
      InfiniteScroll({
        loadMore: () => loadMoreHackathons(),
        lastPageReachedRef: lastPageReached,
        distanceFromBottom: 500, // should be taking into account the footer height (especially for mobile)
        scrollThrottleDelay: 200
      })
    })

    const searchBarRef = ref(null);

    const hackathons = computed(() => store.getters["hackathons/getHackathons"]);

    const perPage = computed(() => store.getters["hackathons/getPerPage"]);

    return {
      initializing,

      hackathons,

      isLoading: computed(() => store.getters["hackathons/isLoading"]),

      anyPillOpened: computed(() => store.getters["filters/anyPillOpened"]),

      anyHackathons: computed(() => !isEmpty(hackathons.value)),

      currentPage,

      searchAgain() {
        $('html,body').animate({ scrollTop: 0 });
        searchBarRef.value.focusSearchInput();
      },

      showEndOfResults: computed(() => {
        if (mobileView.value)
          return lastPageReached.value && hackathons.value.length >= 1;

        if (!lastPageReached.value) return false;
        // no results loaded yet
        if (perPage.value == null) return false;

        return hackathons.value.length >= perPage.value;
      }),

      searchBarRef,

      lastSearchQuery: computed(() => store.getters["filters/getLastSearchQuery"]),

      fuzzySearch: computed(() => store.getters["hackathons/fuzzySearch"]),
      searchError: computed(() => store.getters["hackathons/searchError"]),

      eligibilityToggleFilter: computed(() => store.getters["filters/getEligibilityFilter"]),

      managedByDevpostBadgeFilter: computed(() => store.getters["filters/getManagedByDevpostBadgeFilter"]),

      mobileView,
      desktopView
    }
  }
}
</script>

<style scoped lang="scss">
  @import 'colors';

  .initializing-state {
    border: 1px solid $grey-2;
    padding: 4rem 0;
  }

  .initializing-state i {
    font-size: 2.5rem;
  }

  #pill-overlay {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.2);
    z-index: 19;
  }

  #results-and-filters {
    position: relative;
  }

  .infinite-scroll-loading i {
    font-size: 2rem;
  }
</style>

<style lang="scss">
  // Reserve space for the results before they render, so the footer
  // doesn't start in the viewport and get pushed down (layout shift)
  #hackathon-search {
    min-height: 100vh;
  }
</style>
